import React, { useCallback, useEffect, useState } from 'react';
import {
  Loader2, Maximize2, X, Pencil, LayoutGrid, User,
  GraduationCap, CalendarCheck, Wallet, MessageSquareWarning,
} from 'lucide-react';
import { studentsApi } from '../students.api';
import type { Student } from '../types';
import type {
  ProfileTabKey, StudentReportCard, StudentAttendance,
  StudentAttendanceSummary, StudentFeeLedger,
} from './profile.types';
import { toastMessage } from '@/utils/toast-message';
import { titleCase } from '@/utils/text';
import StudentForm from '../StudentForm';
import OverviewTab from './tabs/OverviewTab';
import ProfileTab from './tabs/ProfileTab';
import AcademicsTab from './tabs/AcademicsTab';
// import ReportCardsTab from './tabs/ReportCardsTab'; // merged into AcademicsTab
import AttendanceTab from './tabs/AttendanceTab';
import FeesTab from './tabs/FeesTab';
import ConcernsTab from './tabs/ConcernsTab';

// Shared snapshot loaded once per profile — powers hero quick-stats + Overview.
export interface ProfileSnapshot {
  reportCard: StudentReportCard | null;
  attendance: StudentAttendance | null;
  attendanceSummary: StudentAttendanceSummary | null; // last 3 months
  fees: StudentFeeLedger | null;
  loading: boolean;
}

const TABS: { key: ProfileTabKey; label: string; icon: React.ElementType }[] = [
  { key: 'overview',     label: 'Overview',     icon: LayoutGrid },
  { key: 'profile',      label: 'Profile',      icon: User },
  { key: 'academics',    label: 'Academics',    icon: GraduationCap },
  // { key: 'report-cards', label: 'Report Cards', icon: FileText }, // merged into Academics tab
  { key: 'attendance',   label: 'Attendance',   icon: CalendarCheck },
  { key: 'fees',         label: 'Fees',         icon: Wallet },
  { key: 'concerns',     label: 'Concerns',     icon: MessageSquareWarning },
];

const STATUS_BADGE: Record<string, string> = {
  active: 'b-success', admitted: 'b-info', draft: 'b-neutral',
  inactive: 'b-warning', transferred: 'b-warning',
  graduated: 'b-info', alumni: 'b-neutral', dropped: 'b-danger',
};

interface Props {
  uuid: string;
  variant?: 'page' | 'panel';
  onClose?: () => void;   // panel only
  onExpand?: () => void;  // panel only → navigate to full page
}

const StudentProfile: React.FC<Props> = ({ uuid, variant = 'page', onClose, onExpand }) => {
  const [student, setStudent] = useState<Student | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<ProfileTabKey>('overview');
  const [editing, setEditing] = useState(false);
  const [snap, setSnap] = useState<ProfileSnapshot>({
    reportCard: null, attendance: null, attendanceSummary: null, fees: null, loading: true,
  });

  const loadStudent = useCallback(() => {
    setLoading(true);
    studentsApi.get(uuid)
      .then((res) => {
        if (res.success && res.data) setStudent(res.data);
        else toastMessage(res.message || 'Failed to load student', 'error');
      })
      .catch(() => toastMessage('Failed to load student', 'error'))
      .finally(() => setLoading(false));
  }, [uuid]);

  const loadSnapshot = useCallback(() => {
    setSnap((s) => ({ ...s, loading: true }));
    Promise.allSettled([
      studentsApi.reportCard(uuid),
      studentsApi.attendance(uuid),
      studentsApi.fees(uuid),
      studentsApi.attendanceSummary(uuid, 3),
    ]).then(([rc, at, fe, as]) => {
      setSnap({
        reportCard: rc.status === 'fulfilled' && rc.value.success ? rc.value.data ?? null : null,
        attendance: at.status === 'fulfilled' && at.value.success ? at.value.data ?? null : null,
        fees: fe.status === 'fulfilled' && fe.value.success ? fe.value.data ?? null : null,
        attendanceSummary: as.status === 'fulfilled' && as.value.success ? as.value.data ?? null : null,
        loading: false,
      });
    });
  }, [uuid]);

  useEffect(() => { loadStudent(); loadSnapshot(); }, [loadStudent, loadSnapshot]);

  if (loading && !student) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <Loader2 size={28} style={{ animation: 'spin 1s linear infinite', color: 'var(--slate)' }} />
      </div>
    );
  }
  if (!student) return null;

  const cls = student.class?.class_label;
  const sec = student.section?.section_label;
  const attPct = snap.attendance?.attendance.percentage;
  const feeDue = snap.fees?.summary.total_pending;
  // Latest term with a computed percent
  const latestPct = (() => {
    const terms = snap.reportCard?.terms ?? [];
    for (let i = terms.length - 1; i >= 0; i--) {
      if (terms[i].summary.percent != null) return terms[i].summary.percent;
    }
    return null;
  })();

  return (
    <div className="student-profile" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Hero */}
      <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
          {student.photo_url ? (
            <img src={student.photo_url} alt="" className="avatar" style={{ width: 64, height: 64 }} />
          ) : (
            <div className="avatar cat-blue" style={{ width: 64, height: 64, fontSize: 24, fontWeight: 800 }}>
              {student.display_name.charAt(0).toUpperCase()}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px' }}>
                {titleCase(student.display_name)}
              </h1>
              <span className={`badge ${STATUS_BADGE[student.status] || 'b-neutral'}`}>
                <span className="dot" />{titleCase(student.status)}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 16, marginTop: 6, flexWrap: 'wrap', fontSize: 13, color: 'var(--text-2)' }}>
              {(cls || sec) && <span>{cls}{sec ? ` · ${sec}` : ''}</span>}
              <span>Adm. {student.admission_number || '—'}</span>
              <span>Roll {student.roll_number || '—'}</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-soft btn-sm" onClick={() => setEditing(true)}>
              <Pencil size={14} /> Edit
            </button>
            {variant === 'panel' && onExpand && (
              <button className="icon-btn" title="Open full page" onClick={onExpand}>
                <Maximize2 size={17} />
              </button>
            )}
            {variant === 'panel' && onClose && (
              <button className="icon-btn" title="Close" onClick={onClose}>
                <X size={18} />
              </button>
            )}
          </div>
        </div>

        {/* Quick stats */}
        <div style={{ display: 'flex', gap: 22, marginTop: 16, flexWrap: 'wrap' }}>
          <QuickStat label="Attendance (this month)" value={attPct != null ? `${attPct}%` : '—'} loading={snap.loading} />
          <QuickStat label="Latest result" value={latestPct != null ? `${latestPct}%` : '—'} loading={snap.loading} />
          <QuickStat
            label="Fees pending"
            value={feeDue != null ? `₹${feeDue.toLocaleString('en-IN')}` : '—'}
            tone={feeDue && feeDue > 0 ? 'danger' : 'success'}
            loading={snap.loading}
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs" style={{ padding: '0 24px', overflowX: 'auto' }}>
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              className={tab === t.key ? 'active' : ''}
              onClick={() => setTab(t.key)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
            >
              <Icon size={15} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab body */}
      <div style={{ flex: 1, overflow: 'auto', padding: 24, background: 'var(--bg)' }}>
        {tab === 'overview'     && <OverviewTab student={student} snap={snap} onGoTab={setTab} />}
        {tab === 'profile'      && <ProfileTab student={student} onEdit={() => setEditing(true)} />}
        {tab === 'academics'    && <AcademicsTab uuid={uuid} />}
        {/* {tab === 'report-cards' && <ReportCardsTab uuid={uuid} />} — merged into AcademicsTab */}
        {tab === 'attendance'   && <AttendanceTab uuid={uuid} initial={snap.attendance} />}
        {tab === 'fees'         && <FeesTab fees={snap.fees} loading={snap.loading} />}
        {tab === 'concerns'     && <ConcernsTab uuid={uuid} />}
      </div>

      {editing && (
        <StudentForm
          student={{ uuid }}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); loadStudent(); loadSnapshot(); }}
        />
      )}
    </div>
  );
};

function QuickStat({ label, value, tone, loading }: {
  label: string; value: string; tone?: 'danger' | 'success'; loading?: boolean;
}) {
  const color = tone === 'danger' ? 'var(--danger)' : tone === 'success' ? 'var(--success)' : 'var(--text)';
  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.6px', color, fontVariantNumeric: 'tabular-nums' }}>
        {loading ? '…' : value}
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginTop: 3 }}>{label}</div>
    </div>
  );
}

export default StudentProfile;
